import React, { useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Cell, PieChart, Pie, LineChart, Line,
} from 'recharts';

const COLORS = {
  blue: '#0974BA',
  navy: '#2E4974',
  green: '#13983D',
  yellow: '#F9B047',
  red: '#E83A3C',
};
const PALETTE = [
  COLORS.blue, COLORS.green, COLORS.yellow, COLORS.red, COLORS.navy,
  '#7C3AED', '#0891B2', '#DB2777', '#65A30D', '#EA580C',
];

const STATUS_LABELS = {
  active: 'Ativo',
  licenca_maternidade: 'Licença Maternidade',
  licenca_medica: 'Licença Médica',
  licenca_sem_vencimento: 'Licença sem Vencimento',
  licenca_estudo: 'Licença para Estudo',
  cedido: 'Cedido',
  readaptado: 'Readaptado(a)',
  inactive: 'Inativo',
};
const STATUS_COLORS = {
  active: COLORS.green,
  licenca_maternidade: '#7C3AED',
  licenca_medica: COLORS.yellow,
  licenca_sem_vencimento: COLORS.navy,
  licenca_estudo: '#EA580C',
  cedido: COLORS.blue,
  readaptado: '#0891B2',
  inactive: COLORS.red,
};

const EMPLOYMENT_TYPE_LABELS = {
  estatutario: 'Estatutário',
  comissionado: 'Comissionado',
  contratado: 'Contratado',
  terceirizado: 'Terceirizado',
};

function ChartCard({ title, subtitle, children, darkMode, className = '' }) {
  return (
    <div className={`rounded-2xl p-5 border backdrop-blur-xl ${
      darkMode ? 'bg-slate-800/70 border-slate-700/30' : 'bg-white/90 border-slate-200 shadow-sm'
    } ${className}`}>
      <div className="mb-4">
        <h3 className={`text-sm font-semibold ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>{title}</h3>
        {subtitle && (
          <p className={`text-xs mt-0.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{subtitle}</p>
        )}
      </div>
      {children}
    </div>
  );
}

function StatCard({ label, value, color = 'blue', darkMode, icon }) {
  const tones = {
    blue: { text: 'text-[#0974BA]', bg: darkMode ? 'bg-[#0974BA]/10' : 'bg-[#0974BA]/5', border: 'border-[#0974BA]/20' },
    green: { text: 'text-[#13983D]', bg: darkMode ? 'bg-[#13983D]/10' : 'bg-[#13983D]/5', border: 'border-[#13983D]/20' },
    yellow: { text: 'text-[#F9B047]', bg: darkMode ? 'bg-[#F9B047]/10' : 'bg-[#F9B047]/5', border: 'border-[#F9B047]/20' },
    red: { text: 'text-[#E83A3C]', bg: darkMode ? 'bg-[#E83A3C]/10' : 'bg-[#E83A3C]/5', border: 'border-[#E83A3C]/20' },
    navy: { text: 'text-[#2E4974]', bg: darkMode ? 'bg-[#2E4974]/10' : 'bg-[#2E4974]/5', border: 'border-[#2E4974]/20' },
  };
  const t = tones[color] || tones.blue;
  return (
    <div className={`rounded-2xl p-4 border ${t.bg} ${t.border}`}>
      <div className="flex items-center justify-between mb-2">
        <span className={`text-xs font-medium ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{label}</span>
        {icon && <span className="text-lg">{icon}</span>}
      </div>
      <p className={`text-2xl font-bold ${t.text}`}>{value}</p>
    </div>
  );
}

function CustomTooltip({ active, payload, label, darkMode }) {
  if (!active || !payload?.length) return null;
  return (
    <div className={`rounded-lg px-3 py-2 shadow-lg border text-xs ${
      darkMode ? 'bg-slate-800 border-slate-600 text-slate-200' : 'bg-white border-slate-200 text-slate-700'
    }`}>
      {label !== undefined && <p className="font-semibold mb-1">{label}</p>}
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.payload?.color || p.color }}>
          {p.name}: <span className="font-bold">{p.value}</span>
        </p>
      ))}
    </div>
  );
}

function EmptyState({ darkMode, height = 220 }) {
  return (
    <div
      className={`flex items-center justify-center text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}
      style={{ height }}
    >
      Sem dados para exibir
    </div>
  );
}

function PieLabel({ percent, name }) {
  if (percent < 0.06) return null;
  return `${name} ${(percent * 100).toFixed(0)}%`;
}

function DashboardRH({ funcionarios, escolas, teacherSchools, cargos, darkMode }) {
  const axisColor = darkMode ? '#94a3b8' : '#64748b';
  const gridColor = darkMode ? 'rgba(148, 163, 184, 0.15)' : 'rgba(100, 116, 139, 0.15)';

  const stats = useMemo(() => {
    const total = funcionarios.length;
    const ativos = funcionarios.filter(f => f.status === 'active').length;
    const licencas = funcionarios.filter(f => (f.status || '').startsWith('licenca')).length;
    const cedidos = funcionarios.filter(f => f.status === 'cedido').length;
    const inativos = funcionarios.filter(f => f.status === 'inactive').length;
    return { total, ativos, licencas, cedidos, inativos };
  }, [funcionarios]);

  const porStatus = useMemo(() => {
    const counts = funcionarios.reduce((acc, f) => {
      const k = f.status || 'desconhecido';
      acc[k] = (acc[k] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(counts)
      .map(([k, v]) => ({
        name: STATUS_LABELS[k] || k,
        value: v,
        color: STATUS_COLORS[k] || COLORS.blue,
      }))
      .sort((a, b) => b.value - a.value);
  }, [funcionarios]);

  const porCargo = useMemo(() => {
    const counts = funcionarios.reduce((acc, f) => {
      const k = f.role_name || 'Sem cargo';
      acc[k] = (acc[k] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(counts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [funcionarios]);

  const porVinculo = useMemo(() => {
    const counts = funcionarios.reduce((acc, f) => {
      const k = f.employment_type || 'nao_informado';
      acc[k] = (acc[k] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(counts).map(([k, v], i) => ({
      name: EMPLOYMENT_TYPE_LABELS[k] || 'Não informado',
      value: v,
      color: PALETTE[i % PALETTE.length],
    }));
  }, [funcionarios]);

  const porEscola = useMemo(() => {
    const schoolNameById = new Map(escolas.map(s => [s.id, s.school || s.name]));
    const employeeStatusById = new Map(funcionarios.map(f => [f.id, f.status]));

    const counts = new Map();
    teacherSchools.forEach(ts => {
      const empId = ts.id_teacher;
      const schoolId = ts.id_school;
      const status = employeeStatusById.get(empId);
      if (!status) return;
      const name = schoolNameById.get(schoolId);
      if (!name) return;
      const cur = counts.get(name) || { name, ativos: 0, outros: 0, total: 0 };
      cur.total += 1;
      if (status === 'active') cur.ativos += 1;
      else cur.outros += 1;
      counts.set(name, cur);
    });
    return Array.from(counts.values())
      .sort((a, b) => b.total - a.total)
      .slice(0, 12);
  }, [escolas, funcionarios, teacherSchools]);

  const cargoPorStatus = useMemo(() => {
    const TOP = 6;
    const top = porCargo.slice(0, TOP).map(c => c.name);
    const result = top.map(name => ({ name, ativos: 0, licencas: 0, outros: 0 }));
    const idx = new Map(result.map((r, i) => [r.name, i]));
    funcionarios.forEach(f => {
      const role = f.role_name || 'Sem cargo';
      const i = idx.get(role);
      if (i === undefined) return;
      if (f.status === 'active') result[i].ativos += 1;
      else if ((f.status || '').startsWith('licenca')) result[i].licencas += 1;
      else result[i].outros += 1;
    });
    return result;
  }, [funcionarios, porCargo]);

  const admissoesPorAno = useMemo(() => {
    const counts = funcionarios.reduce((acc, f) => {
      if (!f.hire_date) return acc;
      const year = String(f.hire_date).slice(0, 4);
      if (!/^\d{4}$/.test(year)) return acc;
      acc[year] = (acc[year] || 0) + 1;
      return acc;
    }, {});
    const years = Object.keys(counts).sort();
    return years.map(y => ({ ano: y, admissoes: counts[y] }));
  }, [funcionarios]);

  const semEscola = useMemo(() => {
    const employeesWithSchool = new Set(teacherSchools.map(ts => ts.id_teacher));
    return funcionarios.filter(f => !employeesWithSchool.has(f.id)).length;
  }, [funcionarios, teacherSchools]);

  if (funcionarios.length === 0) return null;

  return (
    <div className="space-y-5 mb-6">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard label="Total" value={stats.total} color="blue" icon="👥" darkMode={darkMode} />
        <StatCard label="Ativos" value={stats.ativos} color="green" icon="✅" darkMode={darkMode} />
        <StatCard label="Em licença" value={stats.licencas} color="yellow" icon="🩺" darkMode={darkMode} />
        <StatCard label="Cedidos" value={stats.cedidos} color="navy" icon="🔁" darkMode={darkMode} />
        <StatCard label="Inativos" value={stats.inativos} color="red" icon="⏸️" darkMode={darkMode} />
        <StatCard label="Sem escola" value={semEscola} color="yellow" icon="🏫" darkMode={darkMode} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <ChartCard title="Distribuição por status" subtitle="Situação funcional dos funcionários" darkMode={darkMode}>
          {porStatus.length === 0 ? (
            <EmptyState darkMode={darkMode} />
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={porStatus}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={50}
                  outerRadius={90}
                  paddingAngle={2}
                  label={PieLabel}
                  labelLine={false}
                >
                  {porStatus.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip darkMode={darkMode} />} />
                <Legend
                  wrapperStyle={{ fontSize: 11, color: axisColor }}
                  iconSize={10}
                />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Tipo de vínculo" subtitle="Forma de contratação" darkMode={darkMode}>
          {porVinculo.length === 0 ? (
            <EmptyState darkMode={darkMode} />
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={porVinculo}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={50}
                  outerRadius={90}
                  paddingAngle={2}
                  label={PieLabel}
                  labelLine={false}
                >
                  {porVinculo.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip darkMode={darkMode} />} />
                <Legend wrapperStyle={{ fontSize: 11, color: axisColor }} iconSize={10} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      <ChartCard title="Funcionários por cargo" subtitle="Quantidade total em cada função" darkMode={darkMode}>
        {porCargo.length === 0 ? (
          <EmptyState darkMode={darkMode} />
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(260, porCargo.length * 32)}>
            <BarChart
              data={porCargo}
              layout="vertical"
              margin={{ top: 8, right: 24, left: 8, bottom: 8 }}
            >
              <CartesianGrid stroke={gridColor} horizontal={false} />
              <XAxis type="number" tick={{ fill: axisColor, fontSize: 11 }} allowDecimals={false} />
              <YAxis
                type="category"
                dataKey="name"
                tick={{ fill: axisColor, fontSize: 11 }}
                width={150}
              />
              <Tooltip content={<CustomTooltip darkMode={darkMode} />} cursor={{ fill: gridColor }} />
              <Bar dataKey="value" name="Funcionários" radius={[0, 6, 6, 0]}>
                {porCargo.map((_, i) => (
                  <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      <ChartCard
        title="Funcionários por escola"
        subtitle={porEscola.length >= 12 ? 'Top 12 escolas' : 'Total por unidade'}
        darkMode={darkMode}
      >
        {porEscola.length === 0 ? (
          <EmptyState darkMode={darkMode} />
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(280, porEscola.length * 36)}>
            <BarChart
              data={porEscola}
              layout="vertical"
              margin={{ top: 8, right: 24, left: 8, bottom: 8 }}
            >
              <CartesianGrid stroke={gridColor} horizontal={false} />
              <XAxis type="number" tick={{ fill: axisColor, fontSize: 11 }} allowDecimals={false} />
              <YAxis type="category" dataKey="name" tick={{ fill: axisColor, fontSize: 11 }} width={170} />
              <Tooltip content={<CustomTooltip darkMode={darkMode} />} cursor={{ fill: gridColor }} />
              <Legend wrapperStyle={{ fontSize: 11, color: axisColor }} iconSize={10} />
              <Bar dataKey="ativos" name="Ativos" stackId="a" fill={COLORS.green} radius={[0, 0, 0, 0]} />
              <Bar dataKey="outros" name="Licenças / Outros" stackId="a" fill={COLORS.yellow} radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <ChartCard title="Top cargos × status" subtitle="Comparação dos cargos com mais funcionários" darkMode={darkMode}>
          {cargoPorStatus.length === 0 ? (
            <EmptyState darkMode={darkMode} />
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={cargoPorStatus} margin={{ top: 8, right: 16, left: 0, bottom: 40 }}>
                <CartesianGrid stroke={gridColor} vertical={false} />
                <XAxis
                  dataKey="name"
                  tick={{ fill: axisColor, fontSize: 10 }}
                  angle={-25}
                  textAnchor="end"
                  height={60}
                  interval={0}
                />
                <YAxis tick={{ fill: axisColor, fontSize: 11 }} allowDecimals={false} />
                <Tooltip content={<CustomTooltip darkMode={darkMode} />} cursor={{ fill: gridColor }} />
                <Legend wrapperStyle={{ fontSize: 11, color: axisColor }} iconSize={10} />
                <Bar dataKey="ativos" name="Ativos" stackId="b" fill={COLORS.green} />
                <Bar dataKey="licencas" name="Licenças" stackId="b" fill={COLORS.yellow} />
                <Bar dataKey="outros" name="Outros" stackId="b" fill={COLORS.red} radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard
          title="Admissões por ano"
          subtitle="Histórico baseado na data de admissão"
          darkMode={darkMode}
        >
          {admissoesPorAno.length === 0 ? (
            <EmptyState darkMode={darkMode} />
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={admissoesPorAno} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                <CartesianGrid stroke={gridColor} vertical={false} />
                <XAxis dataKey="ano" tick={{ fill: axisColor, fontSize: 11 }} />
                <YAxis tick={{ fill: axisColor, fontSize: 11 }} allowDecimals={false} />
                <Tooltip content={<CustomTooltip darkMode={darkMode} />} cursor={{ stroke: gridColor }} />
                <Line
                  type="monotone"
                  dataKey="admissoes"
                  name="Admissões"
                  stroke={COLORS.blue}
                  strokeWidth={2.5}
                  dot={{ r: 4, fill: COLORS.blue }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>
    </div>
  );
}

export default DashboardRH;
